/** Общие разделы: профиль вебхука, вложения, параметры отправки. */
import { newAttachment } from '../../../src/shared/factory.js';
import { LIMITS } from '../../../src/shared/limits.js';
import { sanitizeFileName } from '../../../src/shared/convert.js';
import { h, icon } from '../dom.js';
import { previewUrl } from '../api.js';
import { store } from '../store.js';
import { mediaField, pickFile, switchField, textField, uploadFile } from '../ui/inputs.js';
import { toast } from '../ui/overlay.js';
import { addButton, emptyHint, itemCard, listActions, row, sectionPanel } from './common.js';
import { cloneWithNewIds } from '../../../src/shared/factory.js';

export function profileSection(): HTMLElement {
  const d = store.draft;
  return sectionPanel({
    id: 'sec-profile',
    title: 'Профиль вебхука',
    icon: 'user',
    defaultCollapsed: true,
    badge: d.profile.username ? d.profile.username : undefined,
    body: () => [
      h('p', { class: 'muted small' }, 'Имя и аватар применяются при отправке через вебхук. От имени бота сообщение всегда идёт с его профилем.'),
      row(
        textField({ label: 'Имя', value: d.profile.username, max: LIMITS.webhookUsername, placeholder: 'Как у бота', onInput: (v) => store.input('profile.name', () => (d.profile.username = v)) }),
      ),
      mediaField('Аватар (ссылка)', d.profile.avatarUrl, (v) => store.input('profile.avatar', () => (d.profile.avatarUrl = v)), {
        hint: 'Discord принимает только https-ссылку на картинку',
      }),
    ],
  });
}

export function attachmentsSection(): HTMLElement {
  const d = store.draft;
  const addFromComputer = async () => {
    const files = await pickFile('*/*', true);
    if (!files.length) return;
    const free = LIMITS.attachments - d.attachments.length;
    for (const f of files.slice(0, free)) {
      try {
        const url = await uploadFile(f);
        const name = uniqueName(sanitizeFileName(f.name));
        store.change(() => d.attachments.push(newAttachment(name, url)));
      } catch (err: any) {
        toast(err.message ?? 'Не удалось загрузить', 'error');
      }
    }
    if (files.length > free) toast(`Добавлено только ${free}: лимит ${LIMITS.attachments} файлов`, 'error');
  };
  const uniqueName = (name: string) => {
    let n = name;
    let i = 1;
    while (d.attachments.some((a) => a.name === n)) {
      const dot = name.lastIndexOf('.');
      n = dot > 0 ? `${name.slice(0, dot)}_${i}${name.slice(dot)}` : `${name}_${i}`;
      i++;
    }
    return n;
  };
  return sectionPanel({
    id: 'sec-attachments',
    title: 'Вложения',
    icon: 'paperclip',
    badge: `${d.attachments.length}/${LIMITS.attachments}`,
    defaultCollapsed: d.attachments.length === 0,
    body: () => [
      d.attachments.length
        ? h(
            'div',
            { class: 'list' },
            d.attachments.map((a, i) =>
              itemCard({
                id: a.id,
                level: 1,
                icon: 'file',
                title: a.name || `Файл ${i + 1}`,
                subtitle: a.source.startsWith('upload://') ? 'загружен с компьютера' : a.source,
                actions: listActions(d.attachments, i, (x) => cloneWithNewIds(x)),
                body: () => [
                  row(
                    textField({
                      label: 'Имя файла',
                      value: a.name,
                      mono: true,
                      required: true,
                      hint: `Ссылка внутри сообщения: attachment://${a.name}`,
                      onInput: (v) => store.input(`${a.id}.name`, () => renameAttachment(a.name, v, a)),
                    }),
                  ),
                  mediaField('Источник', a.source, (v) => store.input(`${a.id}.src`, () => (a.source = v)), { accept: '*/*', hint: 'https-ссылка (бот скачает файл при отправке) или загрузка' }),
                  textField({ label: 'Описание (alt-текст)', value: a.description, max: LIMITS.attachmentDescription, onInput: (v) => store.input(`${a.id}.desc`, () => (a.description = v)) }),
                  switchField('Спойлер', a.spoiler, (v) => store.input(`${a.id}.spoiler`, () => (a.spoiler = v))),
                  a.source && /\.(png|jpe?g|gif|webp)$/i.test(a.name) ? h('img', { class: 'attach-preview', src: previewUrl(a.source), alt: '' }) : null,
                ],
              }),
            ),
          )
        : emptyHint('Файлы прикрепляются к сообщению. В V2 их показывает компонент «Файл» или галерея (attachment://имя).'),
      h(
        'div',
        { class: 'btn-row' },
        addButton('С компьютера', addFromComputer, { small: true, disabled: d.attachments.length >= LIMITS.attachments }),
        addButton('По ссылке', () => store.change(() => d.attachments.push(newAttachment(uniqueName('file.png'), ''))), {
          small: true,
          disabled: d.attachments.length >= LIMITS.attachments,
        }),
      ),
    ],
  });
}

/** При переименовании вложения обновляем ссылки attachment://старое -> новое. */
function renameAttachment(oldName: string, newName: string, a: { name: string }) {
  a.name = newName;
  const d = store.draft;
  const fix = (s: string) => (s === `attachment://${oldName}` ? `attachment://${newName}` : s);
  for (const e of d.embeds) {
    e.image = fix(e.image);
    e.thumbnail = fix(e.thumbnail);
    e.author.iconUrl = fix(e.author.iconUrl);
    e.footer.iconUrl = fix(e.footer.iconUrl);
  }
  const walk = (c: any) => {
    if (c.type === 'file' && c.attachment === oldName) c.attachment = newName;
    if (c.type === 'gallery') c.items.forEach((it: any) => (it.url = fix(it.url)));
    if (c.type === 'section' && c.accessory.type === 'thumbnail') c.accessory.url = fix(c.accessory.url);
    if (c.type === 'container') c.children.forEach(walk);
  };
  d.v2.forEach(walk);
}

export function optionsSection(): HTMLElement {
  const d = store.draft;
  const o = d.options;
  return sectionPanel({
    id: 'sec-options',
    title: 'Параметры отправки',
    icon: 'settings',
    defaultCollapsed: true,
    body: () => [
      switchField('Без уведомления (@silent)', o.silent, (v) => store.input('opt.silent', () => (o.silent = v)), 'Участники не получат push-уведомление'),
      d.mode === 'classic'
        ? switchField('Скрыть превью ссылок', o.suppressEmbeds, (v) => store.input('opt.suppress', () => (o.suppressEmbeds = v)), 'Не показывать карточки ссылок и эмбеды')
        : null,
      h('div', { class: 'group-title' }, 'Разрешённые упоминания'),
      ...(d.mode === 'v2'
        ? [h('p', { class: 'muted small' }, icon('info', 13), ' В Components V2 упоминания отображаются ссылками, но никого не пингуют: пользователи и роли не получают уведомлений.')]
        : [
            switchField('Пользователи', o.mentions.users, (v) => store.input('opt.mu', () => (o.mentions.users = v))),
            switchField('Роли', o.mentions.roles, (v) => store.input('opt.mr', () => (o.mentions.roles = v))),
            switchField('@everyone и @here', o.mentions.everyone, (v) => store.input('opt.me', () => (o.mentions.everyone = v)), 'Сработает, только если у вас есть право упоминать всех'),
            h('p', { class: 'muted small' }, icon('info', 13), ' Выключенные упоминания отображаются в тексте, но никого не пингуют.'),
          ]),
    ],
  });
}
